import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { map, Subject } from "rxjs";
import { BetPlacementService } from "../../../../../../../../../shared/src/lib/services/bet-placement.service";
import { DecimalPipe, NgClass } from "@angular/common";
import { MarketService } from "../../../../../../../../../shared/src/lib/services/market.service";
import { safeParseFloat } from "../../../../../../../../../shared/src/lib/utils/common";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-ah-ladder-pnl",
  imports: [NgClass, DecimalPipe],
  templateUrl: "./ah-ladder-pnl.component.html",
  styleUrl: "./ah-ladder-pnl.component.scss",
})
export class AhLadderPnlComponent implements OnInit, OnDestroy {
  @Input() bet: any;
  readonly destroy$ = new Subject<void>();
  betPlacementService = inject(BetPlacementService);
  marketService = inject(MarketService);
  conditionalPnLs: any[] = [];

  ngOnInit() {
    this.getCurrentPlacement();
    if (this.bet) this.getMarketForBet(this.bet);
  }

  getMarketForBet(bet: any) {
    this.betPlacementService.handicapMarketsForPnLCalc$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (markets) => {
        if (!markets?.size) return;
        const market = markets.get(bet?.marketId);
        // console.log(bet?.runnerName, market);
        const runners = market?.runners;
        const betOrder = {
          HANDICAP: bet?.handicap,
          betRunner: runners?.find((runner: any) => runner?.selectionId === bet?.selectionId && runner?.handicap === bet?.handicap),
          runners,
          SIDE: bet?.side,
          price: safeParseFloat(bet?.sizeMatched) === 0 ? bet?.priceSize?.price : bet?.averagePriceMatched,
          stake: safeParseFloat(bet?.betSize),
          SPORT_ID: market?.eventType?.id,
        };

        if (["Handicap", "Asian Handicap"].includes(bet?.marketName)) {
          this.determineAHCPnL(betOrder);
        } else {
          this.determineGLPnL(betOrder);
        }
      },
    });
  }

  getCurrentPlacement() {
    this.betPlacementService
      .getCurrentPlacement()
      .pipe(
        map((currentPlacement) => {
          // console.log(currentPlacement);
          if (currentPlacement?.data && !this.bet)
            if (["Handicap", "Asian Handicap"].includes(currentPlacement?.data?.marketName)) {
              this.determineAHCPnL(currentPlacement?.data);
            } else {
              this.determineGLPnL(currentPlacement?.data);
            }
        }),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  determineAHCPnL(placement: any) {
    const conditions = this.formulateACHConditions(placement);

    const isBack = placement?.SIDE === "BACK";
    const isNegative = placement?.HANDICAP < 0;
    const hcpValues = placement?.betRunner?.handicapValues;
    const isStakeSplit = hcpValues?.length === 2;

    const stake = isStakeSplit ? placement?.stake / 2 : placement?.stake;
    const calculatedPnL = (placement?.price - 1) * stake;
    const profit = isBack ? calculatedPnL : stake;
    const loss = isBack ? stake : calculatedPnL;

    this.conditionalPnLs = conditions?.map((condition: any) => {
      let pnl = 0;
      hcpValues?.forEach((value: any) => {
        const absHcpValue = Math.abs(value);
        if (isNegative) {
          if (condition?.goals < absHcpValue || !condition?.goals) {
            pnl += isBack ? -loss : profit;
          } else if (condition?.goals > absHcpValue) {
            pnl += isBack ? profit : -loss;
          }
        } else {
          if (condition?.goals < absHcpValue || !condition?.goals) {
            pnl += isBack ? profit : -loss;
          } else if (condition?.goals > absHcpValue) {
            pnl += isBack ? -loss : profit;
          }
        }
      });

      return { ...condition, pnl };
    });
  }

  determineGLPnL(placement: any) {
    const conditions = this.formulateGLConditions(placement);

    const isBack = placement?.SIDE === "BACK";
    const isOver = placement?.betRunner?.runnerName?.includes("Over");
    const hcpValues = placement?.betRunner?.handicapValues;
    const isStakeSplit = hcpValues?.length === 2;

    const stake = isStakeSplit ? placement?.stake / 2 : placement?.stake;
    const calculatedPnL = (placement?.price - 1) * stake;
    const profit = isBack ? calculatedPnL : stake;
    const loss = isBack ? stake : calculatedPnL;

    this.conditionalPnLs = conditions?.map((condition: any) => {
      let pnl = 0;
      hcpValues?.forEach((value: any) => {
        const absHcpValue = Math.abs(value);
        if (isOver) {
          if (condition?.goals < absHcpValue || !condition?.goals) {
            pnl += isBack ? -loss : profit;
          } else if (condition?.goals > absHcpValue) {
            pnl += isBack ? profit : -loss;
          }
        } else {
          if (condition?.goals < absHcpValue || !condition?.goals) {
            pnl += isBack ? profit : -loss;
          } else if (condition?.goals > absHcpValue) {
            pnl += isBack ? -loss : profit;
          }
        }
      });

      return { ...condition, pnl };
    });
  }

  formulateACHConditions(placement: any) {
    const { HANDICAP: handicap, betRunner: placementRunner, runners } = placement || {};
    const isNegative = handicap < 0;

    const otherRunner = runners?.find((runner: any) => runner?.runnerName !== placementRunner?.runnerName);

    const absHandicaps = placementRunner?.handicapValues?.map(Math.abs) || [];
    const maxHcp = absHandicaps.length ? Math.max(...absHandicaps) : 0;
    const minHcp = absHandicaps.length ? Math.min(...absHandicaps) : 0;

    const maxRange = Number.isInteger(maxHcp) ? maxHcp + 1 : Math.ceil(maxHcp);
    const minRange = Math.ceil(minHcp);

    const runnerLabel = isNegative ? placementRunner?.runnerName : otherRunner?.runnerName;
    const conditions = [];

    for (let i = -1; i <= maxRange; i++) {
      if (minRange - i === 1) {
        const winText = minRange > 1 ? `by ${minRange} or more` : "";
        conditions.push({
          label: `If ${runnerLabel} do not win ${winText}`.trim(),
        });
      } else if (i >= minRange && (minRange !== 0 || i !== 0)) {
        const moreText = i === maxRange ? "or more" : "";
        conditions.push({
          label: `If ${runnerLabel} win by ${i} ${moreText}`.trim(),
          goals: i,
        });
      }
    }

    return conditions;
  }

  formulateGLConditions(placement: any) {
    // console.log(placement);
    const betRunner = placement?.betRunner;
    const handicapValues = betRunner?.handicapValues || [];

    const maxHcp = handicapValues.length ? Math.max(...handicapValues) : 0;
    const minHcp = handicapValues.length ? Math.min(...handicapValues) : 0;

    const maxRange = Number.isInteger(maxHcp) ? maxHcp + 1 : Math.ceil(maxHcp);
    const minRange = Math.ceil(minHcp);

    const conditions = [];

    const scoreType = placement?.SPORT_ID === "7522" ? "point" : "goal";

    for (let i = 0; i <= maxRange; i++) {
      const isExactOneBelowMin = minRange - i === 1;
      const isValidGoal = i >= minRange && (minRange !== 0 || i !== 0);

      if (isExactOneBelowMin) {
        const winText = minRange > 1 ? "or less" : "";
        conditions.push({
          label: i === 0 ? `Exactly ${i} ${scoreType}s` : `${i} ${scoreType}s ${winText}`,
        });
      } else if (isValidGoal) {
        conditions.push({
          label: i === maxRange ? `${i} ${scoreType}s or more` : `Exactly ${i} ${scoreType}${i > 1 ? "s" : ""}`,
          goals: i,
        });
      }
    }

    return conditions;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
